import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useState, type FormEvent } from "react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Field, Input } from "../components/ui/form";
import { authClient } from "../lib/auth-client";
import { AuthLayout } from "./auth-layout";

export function ResetPasswordPage() {
  const { token, error: linkError } = useSearch({ from: "/reset-password" });
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (password !== confirmation) {
      setError("Passwords do not match");
      return;
    }
    setPending(true);
    setError(null);
    const { error: resetError } = await authClient.resetPassword({
      newPassword: password,
      token: token ?? "",
    });
    setPending(false);
    if (resetError) {
      setError(resetError.message ?? "Could not reset the password");
      return;
    }
    toast.success("Password updated");
    await navigate({ to: "/login" });
  }

  const invalid = !token || linkError;

  return (
    <AuthLayout
      title="Choose a new password"
      footer={
        <Link to="/login" className="hover:text-body">
          Back to sign in
        </Link>
      }
    >
      {invalid ? (
        <p className="text-danger-text text-ui leading-relaxed">
          This reset link is invalid or has expired. Request a new one from the sign-in page.
        </p>
      ) : (
        <form className="flex flex-col gap-3" onSubmit={submit}>
          <Field label="New password" hint="At least 12 characters.">
            <Input
              type="password"
              autoComplete="new-password"
              required
              minLength={12}
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
          </Field>
          <Field label="Repeat password">
            <Input
              type="password"
              autoComplete="new-password"
              required
              minLength={12}
              value={confirmation}
              onChange={(event) => setConfirmation(event.target.value)}
            />
          </Field>
          {error && <p className="text-danger-text text-caption">{error}</p>}
          <Button type="submit" variant="primary" disabled={pending} className="mt-1">
            {pending ? "Saving…" : "Save password"}
          </Button>
        </form>
      )}
    </AuthLayout>
  );
}
